/**
 * Shared, lightweight input-format validators for participant-typed text
 * fields (Detailed Information workflow forms + the Final Statement
 * signature). Deliberately dependency-free (no yup/zod) to match the existing
 * plain-React-state validation style used across the question forms — each
 * validator is a pure predicate over a trimmed string, so callers can wire it
 * into their own `canSave`/`disabled` gating exactly like the existing
 * required-field checks.
 */

/** RFC-5322-adjacent, intentionally permissive email format check. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** True when `value` (trimmed) looks like a valid email address. */
export const isValidEmail = (value: string): boolean =>
  EMAIL_PATTERN.test(value.trim());

/** Earliest model year accepted for a reported vehicle. */
export const MIN_VEHICLE_YEAR = 1900;
/** Vehicle years are accepted up to one year ahead of today (next model year). */
export const MAX_VEHICLE_YEAR_LOOKAHEAD = 1;

/** True when `value` (trimmed) is a 4-digit year within a plausible range. */
export const isValidVehicleYear = (value: string): boolean => {
  const trimmed = value.trim();
  if (!/^\d{4}$/.test(trimmed)) {
    return false;
  }
  const year = Number(trimmed);
  const maxYear = new Date().getFullYear() + MAX_VEHICLE_YEAR_LOOKAHEAD;
  return year >= MIN_VEHICLE_YEAR && year <= maxYear;
};

/** Any Unicode letter (covers accented/non-English names) — no locale-specific library needed. */
const LETTER_PATTERN = /[A-Za-zÀ-ɏ]/;

/**
 * True when `value` (trimmed) contains at least one letter — i.e. it is not
 * purely numeric/punctuation. Used for name-like fields (a vehicle owner's
 * name, the Accept & Sign first/last name) where digits-only input is never a
 * real name.
 */
export const isNonNumericName = (value: string): boolean =>
  LETTER_PATTERN.test(value.trim());
